// PATH: frontend/src/pages/cisu/api.ts

import dayjs from 'dayjs';
import rest from '@data/restWrapper';

export interface CisuRegistration {
    id: number;
    reservationId: number;
    reservationGuestReferenceId: number | null;
    cisuStatus: number;
    cisuGuestId: string | null;
    cisuLastError: string | null;
    entryNumber: number | null;
    entryYear: number | null;
    cisuRegisteredAt: number | null;
    cisuCheckedOutAt: number | null;
    hasPendingChanges: boolean;
    isDeleted: boolean;
    guestTypeCode: string;
    name: string | null;
    genderCode: string | null;
    dateOfBirth: string | null;
    birthCountryCode: string | null;
    birthPlace: string | null;
    checkInDate: string | null;
    plannedCheckOutDate: string | null;
    travelPurposeCode: string | null;
    arrivalTypeCode: string | null;
    accommodationServiceTypeCode: string | null;
    unitNumber: string | null;
    unitFloor: string | null;
    address: string | null;
    residenceTaxReductionCode: string | null;
    travelAgencyIdentifier: string | null;
    foreignTravelAgencyName: string | null;
    foreignTravelAgencyCountryCode: string | null;
    identifier: string | null;
    birthMunicipality: string | null;
    residenceMunicipalityCode: string | null;
    residencePlaceCode: string | null;
    citizenshipCountryCode: string | null;
    identityCardTypeCode: string | null;
    identityCardNumber: string | null;
    identityCardExpiryDate: string | null;
    identityCardIssuer: string | null;
    visaTypeCode: string | null;
    visaNumber: string | null;
    visaExpiryDate: string | null;
    visaIssuancePlace: string | null;
    entryDate: string | null;
    entryPlace: string | null;
    residencePermitExpiryDate: string | null;
    billNumber: string | null;
    numberOfServices: number | null;
    checkOutNote: string | null;
    createdAt: string;
    updatedAt: string;
    Reservation?: {
        id: number;
        statusEnum: number;
        checkInTimestamp: number;
        checkOutTimestamp: number;
        RoomInfo?: {
            id: number;
            name: string;
        };
    };
    ReservationGuestReference?: {
        id: number;
        Customer?: {
            id: number;
            firstName: string;
            lastName: string;
            nationality: string;
        };
    };
}

export interface CisuRegistrationsResponse {
    registrations: CisuRegistration[];
}

const RESERVATION_STATUS_CLOSED = 4;

export function splitCisuRegistrations(
    registrations: CisuRegistration[],
    from: number,
    to: number,
): { checkInList: CisuRegistration[]; checkOutList: CisuRegistration[] } {
    const fromDate = dayjs(from).startOf('day').valueOf();
    const toDate = dayjs(to).endOf('day').valueOf();

    const checkInList = registrations.filter(
        (reg) =>
            (reg.cisuRegisteredAt !== null && reg.cisuRegisteredAt >= fromDate && reg.cisuRegisteredAt <= toDate) ||
            reg.cisuRegisteredAt === null ||
            (reg.cisuCheckedOutAt !== null && reg.cisuCheckedOutAt >= fromDate && reg.cisuCheckedOutAt <= toDate),
    );

    const checkOutList = registrations.filter(
        (reg) =>
            (reg.cisuStatus === 1 || reg.cisuStatus === 5) &&
            reg.cisuCheckedOutAt === null &&
            reg.Reservation?.statusEnum === RESERVATION_STATUS_CLOSED,
    );

    return { checkInList, checkOutList };
}

export interface CisuStats {
    pending: number;
    waitingForData: number;
    registeredToday: number;
    checkedOutToday: number;
    errors: number;
    errorBreakdown: {
        checkIn: number;
        checkOut: number;
    };
    needsCheckout: number;
}

export interface CisuSettingsStatus {
    enabled: boolean;
    hasApiKey: boolean;
    hasAccommodationId: boolean;
    hasBaseUrl: boolean;
    autoSendOnCheckIn: boolean;
    autoSendOnCheckOut: boolean;
}

export interface SendAllPendingResult {
    total: number;
    success: number;
    failed: number;
    errors: string[];
}

export async function getCisuRegistrations(from?: number, to?: number): Promise<CisuRegistration[]> {
    const result: CisuRegistrationsResponse = await rest('/getCisuRegistrations', 'POST', { from, to });
    return result.registrations || [];
}

export async function getCisuStats(): Promise<CisuStats> {
    return await rest('/getCisuStats', 'GET');
}

export async function getCisuSettingsStatus(): Promise<CisuSettingsStatus> {
    return await rest('/getCisuSettingsStatus', 'GET');
}

export async function sendCisuCheckIn(cisuRegistrationId: number): Promise<any> {
    return await rest('/sendCisuCheckIn', 'POST', { cisuRegistrationId });
}

export async function resendCisuCheckIn(cisuRegistrationId: number): Promise<any> {
    return await rest('/resendCisuCheckIn', 'POST', { cisuRegistrationId });
}

export async function sendCisuCheckOut(cisuRegistrationId: number, numberOfServices?: number): Promise<any> {
    return await rest('/sendCisuCheckOut', 'POST', { cisuRegistrationId, numberOfServices });
}

export async function sendAllPendingCisu(): Promise<SendAllPendingResult> {
    return await rest('/sendAllPendingCisu', 'POST', {});
}
